// src/pages/Carrito/CarritoCompras.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Card, Button, Space, Typography, List, Tag,
  Statistic, Divider, Alert, Progress, App, Collapse, Descriptions,
  Spin, Modal, Checkbox, Badge
} from "antd";
import {
  ShoppingCartOutlined, DeleteOutlined, ClockCircleOutlined,
  CreditCardOutlined, ArrowLeftOutlined, CloseOutlined,
  UserOutlined, InfoCircleOutlined, DownOutlined, MailOutlined,
  PhoneOutlined, IdcardOutlined, SafetyCertificateOutlined,
  GiftOutlined, LoadingOutlined, CheckCircleOutlined, FileTextOutlined,
  IdcardTwoTone, WarningOutlined
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiClock, FiGift, FiCreditCard } from "react-icons/fi";
import EncabezadoPagina from "../../Ui/Cliente/EncabezadoPagina";
import EstadoVacio, { claseBotonPrimario } from "../../Ui/Cliente/EstadoVacio";
import TalleresSugeridos from "../../Ui/Cliente/TalleresSugeridos";
import PasosCompra from "../../Ui/Cliente/PasosCompra";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useCart } from "../../../providers/Cart";
import { useNotify, useWithNotify } from "../../../providers/NotificationProvider";
import useAuthContext from "../../../hooks/useAuthContext";
import OrdenCompra from "../../../service/OrdenCompra";
import Pago from "../../../service/Pago";
import Matriculas from "../../../service/Matriculas";
import InscripcionV2 from "../../../service/inscripcion.service";
import { codigoError, mensajeDeError } from "../../../service/client";

dayjs.extend(duration);

const { Title, Text, Paragraph } = Typography;
const { Countdown } = Statistic;
const { Panel } = Collapse;

// Componente para mostrar horarios con bloques
const MostrarHorarioItem = ({ horario }) => {
  if (horario.bloques && horario.bloques.length > 0) {
    return (
      <div className="space-y-1 flex flex-col">
        {horario.nombre && (
          <div className="text-sm font-semibold text-gray-600 mb-1">
            {horario.nombre}
          </div>
        )}

        {horario.bloques.map((bloque, bloqueIdx) => (
          <Space key={bloque._id || bloque.id || bloqueIdx} wrap size="small">
            {bloque.dias.map((dia, diaIdx) => (
              <Tag
                key={`${bloque.id}-${dia}-${diaIdx}`}
                color="blue"
                className="capitalize text-xs m-0"
              >
                {dia}
              </Tag>
            ))}
            <Tag color="blue" icon={<ClockCircleOutlined />} className="text-xs m-0">
              {bloque.inicio} - {bloque.fin}
            </Tag>
          </Space>
        ))}
      </div>
    );
  }

  return (
    <Space wrap className="mb-2">
      <Tag color="blue" className="capitalize">
        {horario.dia || horario.dias?.join(', ')}
      </Tag>
      <Tag color="blue" icon={<ClockCircleOutlined />}>
        {horario.inicio} - {horario.fin}
      </Tag>
    </Space>
  );
};

export default function CarritoCompras() {
  const [timeLeft, setTimeLeft] = useState(0);
  const [procesandoPago, setProcesandoPago] = useState(false);
  const procesandoPagoRef = useRef(false); // guard sincrónico anti doble-submit
  const [tipoCarrito, setTipoCarrito] = useState('pago');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [matriculasRequeridas, setMatriculasRequeridas] = useState([]);
  // Matrículas que el pago va a sumar solas (inscrito sin matrícula vigente).
  const [matriculasAutomaticas, setMatriculasAutomaticas] = useState([]);
  const [verificandoMatriculas, setVerificandoMatriculas] = useState(false);
  const [erroresMatricula, setErroresMatricula] = useState([]);

  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  const {
    cartItems,
    cartExpiration,
    loading,
    removeFromCart,
    clearCart,
    extendCartTime,
    refreshCart
  } = useCart();

  // Calcular datos del carrito
  const carrito = {
    items: cartItems || [],
    total: (cartItems || []).reduce((sum, item) => {
      if (item.tipo === 'matricula') {
        return sum + (item.precio || 0);
      }
      return sum + (item.planSeleccionado?.valor || 0) + (item.planSeleccionado?.matricula || 0);
    }, 0),
    expiraEn: cartExpiration
  };

  useEffect(() => {
    refreshCart();
  }, []);

  // Verificar matrículas requeridas
  useEffect(() => {
    const verificarMatriculasDelCarrito = async () => {
      if (!cartItems?.length || !user) return;

      setVerificandoMatriculas(true);
      setErroresMatricula([]);
      const matriculasNecesarias = [];
      const automaticas = [];
      const errores = [];

      const recintosConMatriculaEnCarrito = cartItems
        .filter(item => item.tipo === 'matricula')
        .map(item => item.datosMatricula?.recintoId);

      console.log('🎫 Matrículas ya en carrito:', recintosConMatriculaEnCarrito);

      try {
        for (const item of cartItems) {
          if (item.tipo === 'matricula') {
            console.log('⏭️ Saltando item de matrícula');
            continue;
          }

          if (!item || !item.taller) {
            console.warn('Item sin taller en carrito:', item);
            continue;
          }

          // El carrito trae `recinto` sin poblar (solo el id): con `recinto?._id` este
          // chequeo no corría nunca. Ya no bloquea: la cotización suma la matrícula
          // sola (oct-2026), así que acá solo se avisa.
          const recintoId = item.taller.recinto?._id || item.taller.recinto;
          if (item.taller.requiereMatricula && recintoId) {
            if (recintosConMatriculaEnCarrito.includes(recintoId)) continue;

            try {
              const validacion = await Matriculas.ValidarParaInscripcion({
                rut: item.inscrito?.rut || user.rut || user['custom:rut'],
                recintoId
              });

              // El servicio ya devuelve el `data` de la respuesta (antes se leía
              // `validacion.data` y siempre daba undefined).
              const v = validacion?.requiereMatricula !== undefined ? validacion : validacion?.data;
              if (v?.requiereMatricula && !v?.tieneMatricula) {
                const clave = `${recintoId}|${item.inscrito?.rut}`;
                if (!automaticas.some(a => a.clave === clave)) {
                  automaticas.push({
                    clave,
                    recintoNombre: item.taller.recinto?.nombre || 'el recinto',
                    inscrito: item.inscrito?.nombre,
                  });
                }
              }
            } catch (error) {
              console.error(`Error verificando matrícula para ${item.taller?.nombre}:`, error);
            }
          }
        }

        setMatriculasRequeridas(matriculasNecesarias);
        setMatriculasAutomaticas(automaticas);
        setErroresMatricula(errores);
      } catch (error) {
        console.error('Error general verificando matrículas:', error);
      } finally {
        setVerificandoMatriculas(false);
      }
    };

    verificarMatriculasDelCarrito();
  }, [cartItems, user]);

  // Determinar tipo de carrito
  useEffect(() => {
    if (!cartItems?.length) {
      setTipoCarrito('pago');
      return;
    }

    const itemsTalleres = cartItems.filter(item => item.tipo !== 'matricula');
    
    const hayGratuitos = itemsTalleres.some(item =>
      item.planSeleccionado?.plan === 'gratuito' || item.planSeleccionado?.valor === 0
    );
    const hayPagados = itemsTalleres.some(item =>
      item.planSeleccionado?.plan !== 'gratuito' && item.planSeleccionado?.valor > 0
    );

    const hayMatriculas = cartItems.some(item => item.tipo === 'matricula');

    if (hayGratuitos && (hayPagados || hayMatriculas)) {
      setTipoCarrito('mixto');
    } else if (hayGratuitos && !hayPagados && !hayMatriculas) {
      setTipoCarrito('gratuito');
    } else {
      setTipoCarrito('pago');
    }
  }, [cartItems]);

  // Timer del carrito
  useEffect(() => {
    if (!carrito.expiraEn) return;

    // Al expirar, el backend es la autoridad: re-consultamos una sola vez (guard
    // `disparado`) en vez de spamear refreshCart() cada segundo en el borde.
    let disparado = false;
    const interval = setInterval(() => {
      const ahora = dayjs();
      const expira = dayjs(carrito.expiraEn);
      const diff = expira.diff(ahora, 'second');

      setTimeLeft(diff);

      if (diff <= 0 && !disparado) {
        disparado = true;
        refreshCart();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [carrito.expiraEn, refreshCart]);

  const handleEliminarItem = async (index) => {
    modal.confirm({
      title: "Eliminar del carrito",
      content: "¿Estás seguro de eliminar este item?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await removeFromCart(index);
          notify.success("Item eliminado del carrito");
        } catch (error) {
          console.error('Error eliminando item:', error);
          notify.error("No se pudo eliminar el item");
        }
      }
    });
  };

  const handleVaciarCarrito = async () => {
    modal.confirm({
      title: "Vaciar carrito",
      content: "¿Estás seguro de eliminar todos los items?",
      okText: "Vaciar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await clearCart();
          notify.success("Carrito vaciado");
        } catch (error) {
          console.error('Error vaciando carrito:', error);
          notify.error("No se pudo vaciar el carrito");
        }
      }
    });
  };

  const handleComprarMatriculas = () => {
    if (matriculasRequeridas.length === 0) return;

    sessionStorage.setItem('carritoTemporal', JSON.stringify({
      items: cartItems,
      returnUrl: '/carrito'
    }));

    const primeraMatricula = matriculasRequeridas[0];
    navigate(`/matriculas/comprar/${primeraMatricula.recintoId}`);
  };

  const handleProcederPago = async () => {
    if (!carrito || carrito.items.length === 0) {
      notify.warning("Tu carrito está vacío");
      return;
    }

    if (matriculasRequeridas.length > 0) {
      modal.warning({
        title: "Matrículas requeridas",
        content: (
          <div>
            <p className="mb-3">Algunos talleres en tu carrito requieren matrícula del recinto:</p>
            <ul className="list-disc pl-5 space-y-2">
              {matriculasRequeridas.map((mat, idx) => (
                <li key={idx}>
                  <strong>{mat.recintoNombre}</strong> - ${mat.valorMatricula.toLocaleString('es-CL')}
                </li>
              ))}
            </ul>
            <p className="mt-3">Debes comprar las matrículas antes de inscribirte en estos talleres.</p>
          </div>
        ),
        okText: "Comprar matrículas",
        cancelText: "Cancelar",
        onOk: handleComprarMatriculas
      });
      return;
    }

    if (!acceptedTerms) {
      notify.error("Debes aceptar los términos y condiciones para continuar");
      return;
    }

    if (timeLeft < 120 && timeLeft > 0) {
      modal.confirm({
        title: "Tiempo de reserva bajo",
        content: (
          <div>
            <p>Te quedan menos de 2 minutos de reserva.</p>
            <p>¿Deseas extender el tiempo antes de continuar?</p>
          </div>
        ),
        okText: "Extender y continuar",
        cancelText: "Continuar sin extender",
        onOk: async () => {
          try {
            await extendCartTime();
            await refreshCart();
            notify.success("Tiempo extendido");
            navigate('/checkout');
          } catch (error) {
            console.error('Error extendiendo tiempo:', error);
            notify.error("No se pudo extender el tiempo");
          }
        },
        onCancel: () => {
          navigate('/checkout');
        }
      });
    } else {
      navigate('/checkout');
    }
  };

  const procesarPagoDirecto = async () => {
    // ⛔ Guard sincrónico: evita doble-invocación (doble-click / re-render) antes de
    // que setProcesandoPago(true) se refleje. La protección definitiva contra
    // duplicados es la idempotencia backend; esto es defensa en profundidad UX.
    if (procesandoPagoRef.current) return;
    procesandoPagoRef.current = true;
    try { await procesarPagoDirectoV2(); }
    finally { procesandoPagoRef.current = false; }
  };

  // Flujo v1 legacy (ya no se invoca; el flujo activo es procesarPagoDirectoV2).
  const procesarPagoDirectoLegacyV1 = async () => {
    try {
      setProcesandoPago(true);

      const ordenResponse = await withNotify(
        OrdenCompra.Crear(
          {
            nombre: user.name || user.dynamoProfile?.name || '',
            rut: user.rut || user['custom:rut'] || user.dynamoProfile?.rut || '',
            email: user.email || '',
            telefono: user.phoneNumber || user.phone || user.dynamoProfile?.phone || ''
          },
          false,
          'boleta'
        ),
        {
          loading: "Creando orden de compra...",
          success: "Orden creada exitosamente",
          error: (error) => error.error || "Error al crear la orden"
        }
      );

      const ordenId = ordenResponse._id || ordenResponse.id;
      const pagoResponse = await Pago.IniciarPago(ordenId);

      if (tipoCarrito === 'gratuito' || carrito.total === 0) {
        notify.success('¡Inscripción confirmada exitosamente!');
        setTimeout(() => {
          navigate('/mis-talleres');
        }, 1500);
        return;
      }

      const { formHtml } = pagoResponse;

      if (!formHtml) {
        throw new Error('No se recibió el formulario de pago');
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formHtml;
      document.body.appendChild(tempDiv);

      const form = tempDiv.querySelector('form');
      if (form) {
        form.submit();
      } else {
        throw new Error('No se encontró el formulario de WebPay');
      }

    } catch (error) {
      console.error('❌ Error procesando pago:', error);
      notify.error(error.error || error.message || "Error al procesar el pago");
      setProcesandoPago(false);
      procesandoPagoRef.current = false; // liberar solo en error (en éxito hay redirect)
    }
  };

  /**
   * 🆕 v2: cotizar → reservar → iniciar-pago. Reemplaza al flujo v1 que
   * creaba la orden con precio del frontend (vulnerable a TV silencioso).
   *
   * - Cotización firmada en backend con hash sha256 (TTL 10min).
   * - Reservas atómicas de cupos (no race condition).
   * - Webpay iniciado con monto firmado (no manipulable).
   * - Callback idempotente en /v2/checkout/return.
   */
  const procesarPagoDirectoV2 = async () => {
    try {
      setProcesandoPago(true);

      const datosCliente = {
        nombre: user.name || user.dynamoProfile?.name || '',
        rut: user.rut || user['custom:rut'] || user.dynamoProfile?.rut || '',
        email: user.email || '',
        telefono: user.phoneNumber || user.phone || user.dynamoProfile?.phone || '',
        comuna: 'Ñuñoa',
        ciudad: 'Santiago'
      };

      // Items del carrito → formato /v2/cotizar
      const items = (cartItems || []).map((it) => {
        if (it.tipo === 'matricula' || it.datosMatricula) {
          return {
            tipo: 'matricula',
            recinto: it.datosMatricula?.recintoId || it.recinto,
            inscrito: {
              rut: it.inscrito?.rut || it.datosMatricula?.usuarioData?.rut,
              nombre: it.inscrito?.nombre || it.datosMatricula?.usuarioData?.nombre,
              email: it.inscrito?.email || it.datosMatricula?.usuarioData?.email,
              telefono: it.inscrito?.telefono || it.datosMatricula?.usuarioData?.telefono
            }
          };
        }
        return {
          tipo: 'taller',
          taller: it.taller?._id || it.taller,
          tallerNombre: it.taller?.nombre || it.tallerNombre,
          horario: it.horario?._id || it.horario,
          plan: it.planSeleccionado?.plan,
          inscrito: {
            esUsuarioPrincipal: it.inscrito?.esUsuarioPrincipal,
            familiarId: it.inscrito?.familiarId,
            nombre: it.inscrito?.nombre,
            rut: it.inscrito?.rut,
            email: it.inscrito?.email,
            telefono: it.inscrito?.telefono,
            edad: it.inscrito?.edad,
            fechaNacimiento: it.inscrito?.fechaNacimiento,
            genero: it.inscrito?.genero
          }
        };
      });

      // 1) Cotizar (incluye validación TV bloqueante por inscrito)
      let cotizacion;
      try {
        cotizacion = await InscripcionV2.cotizar({
          items,
          datosCliente
        });
      } catch (err) {
        const code = codigoError(err);
        if (code === 'TV_API_NO_RESPONDE') {
          notify.error('No pudimos verificar tu Tarjeta Vecino. Por seguridad, no podemos procesar el pago. Intenta de nuevo en unos segundos.');
        } else {
          notify.error(mensajeDeError(err, 'No se pudo cotizar tu compra'));
        }
        setProcesandoPago(false);
        return;
      }

      // 2) Reservar cupos atómicamente
      try {
        await InscripcionV2.reservar(cotizacion.hash);
      } catch (err) {
        const code = codigoError(err);
        if (code === 'CUPO_AGOTADO') {
          notify.error('El cupo se agotó mientras estabas comprando. Vuelve al carrito para elegir otro horario.');
        } else {
          notify.error(mensajeDeError(err, 'No se pudo reservar el cupo'));
        }
        setProcesandoPago(false);
        return;
      }

      // 3) Iniciar Webpay
      const returnUrl = `${window.location.origin}/v2/checkout/return`;
      let pago;
      try {
        pago = await InscripcionV2.iniciarPago({
          hash: cotizacion.hash,
          returnUrl,
          datosCliente
        });
      } catch (err) {
        notify.error(mensajeDeError(err, 'No se pudo iniciar el pago'));
        setProcesandoPago(false);
        return;
      }

      // 4) Submit form a Webpay
      if (!pago?.url || !pago?.token) {
        notify.error('Webpay no devolvió datos para redirigir. Intenta de nuevo.');
        setProcesandoPago(false);
        return;
      }
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = pago.url;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token_ws';
      input.value = pago.token;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      console.error('❌ [v2] Error procesando pago:', error);
      notify.error(mensajeDeError(error, 'Error al procesar el pago'));
      setProcesandoPago(false);
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const hoy = dayjs();
    const fechaNac = dayjs(fechaNacimiento);
    return hoy.diff(fechaNac, 'year');
  };

  const tiempoTotal = 15 * 60;
  const porcentajeTiempo = timeLeft > 0 ? (timeLeft / tiempoTotal) * 100 : 0;

  if (procesandoPago) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="text-center" style={{ minWidth: 400 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} size="large" />
          <div className="mt-6">
            <Title level={3}>
              {tipoCarrito === 'gratuito' ? 'Procesando inscripción...' : 'Redirigiendo a WebPay...'}
            </Title>
            <Text type="secondary" className="text-lg">
              Por favor espera un momento
            </Text>
            <Alert
              message="No cierres esta ventana"
              description={tipoCarrito === 'gratuito'
                ? "Estamos procesando tu inscripción"
                : "Serás redirigido a la plataforma de pago seguro"
              }
              type="info"
              showIcon
              className="mt-4"
            />
          </div>
        </Card>
      </div>
    );
  }

  const vacio = !carrito || !carrito.items || carrito.items.length === 0;

  if (loading || verificandoMatriculas) {
    return (
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <EncabezadoPagina icon={<ShoppingCartOutlined />} titulo="Mi" destacado="carrito" />
          <div className="flex flex-col items-center justify-center gap-4 py-24 rounded-3xl bg-white">
            <Spin size="large" />
            <p className="text-sm font-semibold text-gray-500">
              {verificandoMatriculas ? "Verificando matrículas…" : "Cargando tu carrito…"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (vacio) {
    return (
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <EncabezadoPagina icon={<ShoppingCartOutlined />} titulo="Mi" destacado="carrito"
            subtitulo="Aquí juntas los talleres antes de inscribirte" />
          <PasosCompra actual={0} />
          <EstadoVacio
            icon={<ShoppingCartOutlined />}
            titulo="Tu carrito está vacío"
            texto="Elige un taller y agrégalo al carrito. Tu cupo queda reservado por 15 minutos mientras completas la inscripción."
            acciones={
              <Link to="/#talleres-section" className={claseBotonPrimario}>
                Ver talleres disponibles <FiArrowRight size={15} />
              </Link>
            }
          >
            <ul className="mt-10 pt-8 border-t border-gray-100 w-full max-w-2xl grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              {[
                { icon: <FiClock size={18} />, titulo: "Cupo reservado", texto: "15 minutos desde que agregas" },
                { icon: <FiGift size={18} />, titulo: "Talleres gratis", texto: "Se confirman sin pago" },
                { icon: <FiCreditCard size={18} />, titulo: "Pago seguro", texto: "Con WebPay" },
              ].map(b => (
                <li key={b.titulo} className="flex items-start gap-3">
                  <span className="w-10 h-10 flex-shrink-0 rounded-xl bg-secondary/30 text-[#00266b] flex items-center justify-center">
                    {b.icon}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-gray-900">{b.titulo}</span>
                    <span className="block text-xs text-gray-500">{b.texto}</span>
                  </span>
                </li>
              ))}
            </ul>
          </EstadoVacio>
          <TalleresSugeridos titulo="Talleres para" destacado="empezar" />
        </div>
      </div>
    );
  }

  const nMatriculas = carrito.items.filter(i => i?.tipo === 'matricula').length;
  const nTalleres = carrito.items.length - nMatriculas;
  const resumenItems = [
    nTalleres > 0 && `${nTalleres} taller${nTalleres !== 1 ? 'es' : ''}`,
    nMatriculas > 0 && `${nMatriculas} matrícula${nMatriculas !== 1 ? 's' : ''}`,
  ].filter(Boolean).join(' y ');

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <EncabezadoPagina icon={<ShoppingCartOutlined />} titulo="Mi" destacado="carrito"
          subtitulo={`${resumenItems} en tu carrito`} />
        <PasosCompra actual={1} />

        {matriculasRequeridas.length > 0 && (
          <Alert
            message="⚠️ Matrículas requeridas"
            description={
              <div>
                <p className="mb-2">Algunos talleres requieren matrícula del recinto:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {matriculasRequeridas.map((mat, idx) => (
                    <li key={idx}>
                      <strong>{mat.recintoNombre}</strong> - ${mat.valorMatricula.toLocaleString('es-CL')}
                    </li>
                  ))}
                </ul>
                <Button
                  type="primary"
                  size="small"
                  icon={<IdcardTwoTone />}
                  onClick={handleComprarMatriculas}
                  className="mt-3"
                >
                  Comprar matrículas ahora
                </Button>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            className="mb-6"
          />
        )}

        {matriculasAutomaticas.length > 0 && (
          <Alert
            message="Se sumará la matrícula del recinto"
            description={
              <ul className="list-disc pl-5 space-y-1 m-0">
                {matriculasAutomaticas.map(m => (
                  <li key={m.clave}>
                    <strong>{m.inscrito || 'La persona inscrita'}</strong> no tiene matrícula vigente en <strong>{m.recintoNombre}</strong>: se suma al pagar (verás el valor en el resumen).
                  </li>
                ))}
              </ul>
            }
            type="info"
            showIcon
            icon={<IdcardTwoTone />}
            className="mb-6"
          />
        )}

        {tipoCarrito === 'gratuito' && (
          <Alert
            message="¡Inscripciones Gratuitas!"
            description="Todas las inscripciones en tu carrito son gratuitas. Solo confirma para completar."
            type="success"
            icon={<GiftOutlined />}
            showIcon
            className="mb-6"
          />
        )}

        {tipoCarrito === 'mixto' && (
          <Alert
            message="Carrito Mixto"
            description="Tu carrito contiene inscripciones gratuitas y de pago. Procederás al pago solo por las inscripciones de pago."
            type="info"
            showIcon
            className="mb-6"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {carrito.expiraEn && (
              <Alert
                message={
                  <div className="flex items-center justify-between">
                    <Space>
                      <ClockCircleOutlined />
                      <span>Tiempo restante de reserva</span>
                    </Space>
                  </div>
                }
                description={
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Countdown
                        value={dayjs(carrito.expiraEn).valueOf()}
                        format="mm:ss"
                        valueStyle={{ fontSize: '20px', color: timeLeft < 60 ? '#ff4d4f' : '#0047bb' }}
                      />
                      <Progress
                        percent={porcentajeTiempo}
                        showInfo={false}
                        strokeColor={timeLeft < 60 ? '#ff4d4f' : '#0047bb'}
                        className="flex-1"
                      />
                    </div>
                    <Text type="secondary" className="text-xs">
                      Los cupos quedarán liberados cuando expire el tiempo
                    </Text>
                  </div>
                }
                type={timeLeft < 60 ? 'error' : 'info'}
                showIcon
              />
            )}

            <Card
              title="Items en el carrito"
              extra={
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleVaciarCarrito}
                  loading={loading}
                >
                  Vaciar
                </Button>
              }
            >
              <List
                dataSource={carrito.items}
                renderItem={(item, index) => {
                  if (!item) {
                    console.warn('⚠️ Item es null/undefined en índice:', index);
                    return null;
                  }

                  // RENDERIZAR MATRÍCULA
                  if (item.tipo === 'matricula') {
                    return (
                      <List.Item
                        className="!items-start"
                        actions={[
                          <Button
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleEliminarItem(index)}
                            loading={loading}
                          >
                            Eliminar
                          </Button>
                        ]}
                      >
                        <div className="w-full">
                          <div className="mb-3">
                            <div className="flex items-start gap-2">
                              <IdcardTwoTone style={{ fontSize: '24px' }} className="mt-1" />
                              <div className="flex-1">
                                <Text strong className="text-lg block mb-2">
                                  Matrícula - {item.datosMatricula?.recintoNombre || 'Recinto'}
                                </Text>
                                <Text type="secondary" className="text-sm">
                                  Vigencia: {item.datosMatricula?.configuracionMatricula?.tipoVigencia || 'Anual'}
                                </Text>
                              </div>
                              <Badge count="Matrícula" style={{ backgroundColor: '#0047bb' }} />
                            </div>
                          </div>

                          <div className="mb-3">
                            <Space direction="vertical" size="small" className="w-full">
                              <div className="flex items-center gap-2">
                                <Text type="secondary">Para:</Text>
                                <Tag color="blue">
                                  <UserOutlined className="mr-1" />
                                  {item.inscrito?.nombre || item.datosMatricula?.usuarioData?.nombre || 'Usuario'}
                                </Tag>
                              </div>
                              <div className="flex items-center gap-2">
                                <Text type="secondary">RUT:</Text>
                                <Text>{item.inscrito?.rut || item.datosMatricula?.usuarioData?.rut || 'No especificado'}</Text>
                              </div>
                              <div>
                                <Text strong className="text-lg text-blue-600">
                                  ${(item.precio || item.planSeleccionado?.valor || 0).toLocaleString('es-CL')}
                                </Text>
                              </div>
                            </Space>
                          </div>
                        </div>
                      </List.Item>
                    );
                  }

                  // VALIDAR TALLER
                  if (!item.taller) {
                    console.warn('⚠️ Item sin taller en índice:', index);
                    return (
                      <List.Item
                        actions={[
                          <Button
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleEliminarItem(index)}
                            loading={loading}
                          >
                            Eliminar
                          </Button>
                        ]}
                      >
                        <Alert
                          message="Item no válido"
                          description="Este item no contiene información válida de taller"
                          type="warning"
                          showIcon
                        />
                      </List.Item>
                    );
                  }

                  // RENDERIZAR TALLER
                  const necesitaMatricula = erroresMatricula.find(
                    e => e.taller === item.taller?.nombre
                  );

                  return (
                    <List.Item
                      className="!items-start"
                      actions={[
                        <Button
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleEliminarItem(index)}
                          loading={loading}
                        >
                          Eliminar
                        </Button>
                      ]}
                    >
                      <div className="w-full">
                        <div className="mb-3">
                          <div className="flex items-start gap-2">
                            <Text strong className="text-lg block mb-2 flex-1">
                              {item.taller?.nombre || 'Taller sin nombre'}
                            </Text>
                            {necesitaMatricula && (
                              <Badge count="Requiere matrícula" style={{ backgroundColor: '#faad14' }} />
                            )}
                          </div>

                          {item.horario && <MostrarHorarioItem horario={item.horario} />}
                        </div>

                        {necesitaMatricula && (
                          <Alert
                            message="Matrícula requerida"
                            description={necesitaMatricula.mensaje}
                            type="warning"
                            showIcon
                            icon={<IdcardTwoTone />}
                            className="mb-3"
                            size="small"
                          />
                        )}

                        <div className="mb-3">
                          <Space direction="vertical" size="small" className="w-full">
                            <div className="flex items-center gap-2">
                              <Text type="secondary">Inscrito:</Text>
                              <Tag color={item.tipoInscripcion === 'para_mi' ? 'blue' : 'purple'}>
                                <UserOutlined className="mr-1" />
                                {item.inscrito?.nombre || 'Sin nombre'}
                              </Tag>
                            </div>
                            <div className="flex items-center gap-2">
                              <Text type="secondary">Plan:</Text>
                              <Tag color="blue" className="capitalize">
                                {item.planSeleccionado?.plan || 'Sin plan'}
                              </Tag>
                            </div>
                            <div>
                              {item.planSeleccionado?.plan === 'gratuito' || item.planSeleccionado?.valor === 0 ? (
                                <Tag color="blue" icon={<GiftOutlined />} className="text-base">
                                  GRATIS
                                </Tag>
                              ) : (
                                <Text strong className="text-lg text-blue-600">
                                  ${((item.planSeleccionado?.valor || 0) + (item.planSeleccionado?.matricula || 0)).toLocaleString('es-CL')}
                                </Text>
                              )}
                            </div>
                          </Space>
                        </div>

                        <Collapse
                          ghost
                          size="small"
                          expandIcon={({ isActive }) => (
                            <DownOutlined rotate={isActive ? 180 : 0} />
                          )}
                        >
                          <Panel
                            header={
                              <Space>
                                <InfoCircleOutlined />
                                <Text type="secondary" className="text-sm">
                                  Ver detalles del inscrito
                                </Text>
                              </Space>
                            }
                            key="1"
                          >
                            <Descriptions
                              size="small"
                              column={1}
                              bordered
                              className="bg-gray-50"
                            >
                              <Descriptions.Item label="Nombre completo">
                                {item.inscrito?.nombre || 'No especificado'}
                              </Descriptions.Item>

                              <Descriptions.Item label="RUT">
                                {item.inscrito?.rut || 'No especificado'}
                              </Descriptions.Item>

                              {item.inscrito?.fechaNacimiento && (
                                <Descriptions.Item label="Fecha de nacimiento">
                                  {dayjs(item.inscrito.fechaNacimiento).format('DD/MM/YYYY')}
                                  {calcularEdad(item.inscrito.fechaNacimiento) && (
                                    <Text type="secondary" className="ml-2">
                                      ({calcularEdad(item.inscrito.fechaNacimiento)} años)
                                    </Text>
                                  )}
                                </Descriptions.Item>
                              )}

                              {item.inscrito?.genero && (
                                <Descriptions.Item label="Género">
                                  <Tag className="capitalize">{item.inscrito.genero}</Tag>
                                </Descriptions.Item>
                              )}

                              {item.inscrito?.telefono && (
                                <Descriptions.Item label="Teléfono">
                                  {item.inscrito.telefono}
                                </Descriptions.Item>
                              )}

                              {item.inscrito?.email && (
                                <Descriptions.Item label="Email">
                                  {item.inscrito.email}
                                </Descriptions.Item>
                              )}

                              <Descriptions.Item label="Tipo de inscripción">
                                <Tag color={item.tipoInscripcion === 'para_mi' ? 'blue' : 'purple'}>
                                  {item.tipoInscripcion === 'para_mi' ? 'Inscripción personal' : 'Inscripción familiar'}
                                </Tag>
                              </Descriptions.Item>
                            </Descriptions>
                          </Panel>
                        </Collapse>
                      </div>
                    </List.Item>
                  );
                }}
              />
            </Card>

            <Card title={<><UserOutlined /> Datos del Comprador</>}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={<><UserOutlined /> Nombre</>}>
                  <Text strong>{user?.name || user?.dynamoProfile?.name || 'No especificado'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><IdcardOutlined /> RUT</>}>
                  <Text strong>{user?.rut || user['custom:rut'] || user?.dynamoProfile?.rut || 'No especificado'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><MailOutlined /> Email</>}>
                  <Text strong>{user?.email || 'No especificado'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><PhoneOutlined /> Teléfono</>}>
                  <Text strong>{user?.phoneNumber || user?.phone || user?.dynamoProfile?.phone || 'No especificado'}</Text>
                </Descriptions.Item>
              </Descriptions>

              <Alert
                message="Estos datos se usarán para la compra"
                description="Los datos son tomados de tu perfil y no son modificables en este paso."
                type="info"
                showIcon
                className="mt-4"
                icon={<InfoCircleOutlined />}
              />
            </Card>
          </div>

          <div>
            <Card title="Resumen del pedido" className="sticky top-4">
              <div className="space-y-3">
                {tipoCarrito !== 'gratuito' && (
                  <>
                    <div className="flex justify-between">
                      <Text>Subtotal:</Text>
                      <Text strong>${carrito.total.toLocaleString('es-CL')}</Text>
                    </div>

                    <Divider className="!my-3" />
                  </>
                )}

                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <Text strong className="text-base">Total a pagar</Text>
                  {tipoCarrito === 'gratuito' ? (
                    <Tag color="blue" icon={<GiftOutlined />} className="text-lg px-3 py-1">
                      GRATIS
                    </Tag>
                  ) : (
                    <Text strong className="text-2xl text-blue-600">
                      ${carrito.total.toLocaleString('es-CL')}
                    </Text>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  >
                    <Text>
                      Acepto los{' '}
                      <Button
                        type="link"
                        className="!p-0 !h-auto"
                        icon={<FileTextOutlined />}
                        onClick={() => setShowTermsModal(true)}
                      >
                        Términos y Condiciones
                      </Button>
                    </Text>
                  </Checkbox>
                </div>

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={tipoCarrito === 'gratuito' ? <CheckCircleOutlined /> : <CreditCardOutlined />}
                  onClick={handleProcederPago}
                  className="btn-cta mt-4 h-14 text-lg"
                  loading={loading || procesandoPago}
                  disabled={!acceptedTerms || matriculasRequeridas.length > 0 || procesandoPago}
                >
                  {tipoCarrito === 'gratuito'
                    ? 'Confirmar inscripciones gratuitas'
                    : `Proceder al pago`
                  }
                </Button>

                <Button
                  block
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/talleres')}
                  disabled={procesandoPago}
                >
                  Seguir comprando
                </Button>
              </div>

              <Divider />

              <div className="bg-blue-50 p-3 rounded">
                <Text className="text-xs text-gray-600">
                  <strong>Nota:</strong> Los cupos están reservados temporalmente.
                  Completa tu compra antes de que expire el tiempo.
                </Text>
              </div>

              {tipoCarrito !== 'gratuito' && (
                <div className="mt-4 bg-gray-50 p-3 rounded">
                  <Space direction="vertical" size="small" className="w-full">
                    <Text strong className="text-xs">
                      <SafetyCertificateOutlined /> Pago 100% seguro
                    </Text>
                    <Text type="secondary" className="text-xs">
                      Transbank garantiza la seguridad de tu transacción con encriptación SSL.
                    </Text>
                  </Space>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined />
            <span>Términos y Condiciones</span>
          </div>
        }
        open={showTermsModal}
        onCancel={() => setShowTermsModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowTermsModal(false)}>
            Cerrar
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px 10px' }}>
          <Paragraph>
            Al marcar la casilla término y condiciones, acepta todas las condiciones descritas.
          </Paragraph>

          <Title level={4} className="!mt-6">CONDICIONES PARA NUESTROS USUARIAS Y USUARIOS:</Title>
          <ul style={{ lineHeight: 1.8, listStyleType: 'disc', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Al momento de la inscripción en el taller, cada usuaria y usuario confirman tener una condición de salud compatible con la actividad física.</li>
            <li style={{ marginBottom: '12px' }}>En caso de tener alguna condición médica, es responsabilidad de la usuaria o usuario consultar a su médico por la posibilidad de realizar ejercicios en nuestros recintos.</li>
            <li style={{ marginBottom: '12px' }}>Es importante e imprescindible que, si durante la práctica del ejercicio siente alguna molestia o anomalía física, detenga su actividad y acuda a la consulta de un médico.</li>
            <li style={{ marginBottom: '12px' }}>Cada usuaria y usuario deberá traer su propio candado a fin de resguardar sus pertenencias en los casilleros habilitados. La Corporación Municipal de Deportes no se hará responsable por las pérdidas de las pertenencias en caso de no cumplirse el punto anterior.</li>
            <li style={{ marginBottom: '12px' }}>Una vez inscrita o inscrito en la actividad de preferencia, no habrá devolución de dinero, salvo la presentación de un certificado médico al inicio del taller. Este trámite se realizará solo una vez durante el año.</li>
            <li style={{ marginBottom: '12px' }}>Las suspensiones de clases por motivos climáticos o ambientales declarados para la comuna de Ñuñoa o Región Metropolitana por organismos gubernamentales no son recuperables.</li>
            <li style={{ marginBottom: '12px' }}>Las clases coincidentes con días feriados no son recuperables.</li>
            <li style={{ marginBottom: '12px' }}>Las clases no son recuperables, ya que es responsabilidad de la usuaria o usuario asistir en el horario y día acordado de manera previa.</li>
            <li style={{ marginBottom: '12px' }}>Está prohibido la comercialización u ofrecer cualquier tipo de servicios por medio de folletos y afiches dentro de las instalaciones, incluida el área del estacionamiento. De ser sorprendida/o, no podrá ingresar nuevamente a nuestras dependencias.</li>
            <li style={{ marginBottom: '12px' }}>No se permite el ingreso y consumo de bebidas alcohólicas ni estupefacientes a nuestro recinto. De ser sorprendida/o, no podrá ingresar nuevamente a nuestras dependencias.</li>
            <li style={{ marginBottom: '12px' }}>Está terminantemente prohibido el ingreso de personas portando armas de cualquier tipo. De ser sorprendida/o, la corporación tiene la responsabilidad de informar a Carabineros.</li>
            <li style={{ marginBottom: '12px' }}>No se permite filmar o fotografiar en el interior de las dependencias deportivas, salvo mediante autorización expresa de la administración.</li>
            <li style={{ marginBottom: '12px' }}>Está prohibido fumar en las instalaciones de la corporación.</li>
            <li style={{ marginBottom: '12px' }}>El ingreso a las dependencias de las personas adultas es sin acompañante, salvo que su movilidad requiera un apoyo. En el caso de niñas y niños, podrán ingresar acompañadas/os solo de un adulto.</li>
            <li style={{ marginBottom: '12px' }}>En el caso de que una usuaria o usuario ingrese acompañado de un menor de edad, es responsabilidad del adulto/a el cuidado de la niña, niño o adolescente; aún cuando el adulto/a se encuentre en un taller.</li>
            <li style={{ marginBottom: '12px' }}>Toda usuaria o usuario debe entregar sus datos en la recepción para el ingreso o portar su tarjeta en caso de que corresponda.</li>
            <li style={{ marginBottom: '12px' }}>La Corporación Municipal de Deportes podrá cerrar o modificar los horarios de la oferta deportiva.</li>
            <li style={{ marginBottom: '12px' }}>Es responsabilidad de las usuarias o usuarios el buen uso del espacio deportivo y su equipamiento. En caso de dudas sobre el manejo de máquinas de ejercicios, debe consultar obligatoriamente a la profesora o profesor especialista disponible por parte de la corporación.</li>
            <li style={{ marginBottom: '12px' }}>Quienes incurran en riñas o altercados violentos de diversa índole, ya sea entre usuarias y usuarios o con alguna funcionaria o funcionario de la Corporación Municipal de Deportes de Ñuñoa, su inscripción quedará caducada de forma inmediata, sin la posibilidad de pedir la devolución del dinero pagado por el taller respectivo.</li>
            <li style={{ marginBottom: '12px' }}>Se pondrá término de manera automática a la inscripción de usuarias o usuarios que no cumplan con las reglas estipuladas.</li>
            <li style={{ marginBottom: '12px' }}>En la sala de máquinas no se brinda el servicio de personal trainer internos o externos. Contamos con instructores para evaluar, guiar y supervisar rutinas básicas para mejorar la condición física. En caso de que se sorprenda a una usuaria o usuario realizando este servicio, será cancelada su matrícula junto a la de la persona que contrató el servicio.</li>
            <li style={{ marginBottom: '12px' }}>Quienes incurran en riñas o altercados violentos de diversa índole, así como también en conductas de acoso, ya sea entre usuarias y usuarios o con alguna funcionaria o funcionario de la Corporación Municipal de Deportes de Ñuñoa, su inscripción quedará caducada de forma inmediata, sin la posibilidad de pedir la devolución del dinero pagado por el taller respectivo.</li>
            <li style={{ marginBottom: '12px' }}>La Corporación Municipal de Deportes de Ñuñoa se reserva el derecho de admisión de personas que no mantengan las normas básicas de comportamiento social e higiene personal o evidencien un trato vejatorio hacia funcionarias o funcionarios de esta entidad.</li>
          </ul>

          <Title level={4} className="!mt-6">NORMAS DE INSCRIPCIÓN:</Title>
          <ul style={{ lineHeight: 1.8, listStyleType: 'disc', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '12px' }}>Las cuotas son individuales de cada usuario o usuaria, sin la posibilidad de cederlas a otra persona. En ningún caso las usuarias o usuarios podrán vender, ceder o transferir los derechos del servicio otra persona singular o colectiva. En el caso de que se produzca esta infracción, se sancionará con la anulación de la suscripción sin reintegro de la cuota abonada.</li>
            <li style={{ marginBottom: '12px' }}>Para la inscripción, cada usuaria o usuario deberá ingresar todos sus datos, declarar su estado de salud e inscribir su RUT. En caso de ser vecina o vecino de Ñuñoa, tendrá un valor preferencial en la actividad inscrita.</li>
            <li style={{ marginBottom: '12px' }}>Aquellas usuarias o usuarios que efectúen la inscripción con un RUT falso o de otro residente de la comuna, serán expulsados del taller, se bloqueará el RUT del sistema y se expondrán a posibles acciones legales por fraude por parte de la corporación.</li>
            <li style={{ marginBottom: '12px' }}>Solo pueden acceder a las clases las usuarias y usuarios que hayan abonado sus cuotas correspondientes al taller.</li>
            <li style={{ marginBottom: '12px' }}>Las usuarias y usuarios se comprometen a respetar las normas de inscripción, así como el resto de las normas del servicio.</li>
          </ul>
        </div>
      </Modal>

    </div>
  );
}
